
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import 'iscroll';
import { CoordinatesPosition, HTMLElementWithPosition, Room, SubRoom } from '@/utils/models';
import { v4 } from 'uuid';

import Popup from '@/components/utils/Popup.vue';
import SubRoomResizer from './SubRoomResizer.vue';
import EditSubRoomPropsPopup from './EditSubRoomPropsPopup.vue';
import RoomsMemberList from '@/components/room/rooms-list/RoomsMemberList.vue';
import ConfirmCancellationPopup from '@/components/popup/common/ConfirmCancellationPopup.vue';

@Component({
  components: { Popup, SubRoomResizer, EditSubRoomPropsPopup, RoomsMemberList, ConfirmCancellationPopup },
})
export default class EditSubRoomPopup extends Vue {
  @Prop()
  room!: Room;
  @Emit()
  applySubrooms() {
    return this.subrooms;
  }
  @Emit()
  close() {}
  subrooms: SubRoom[] = [];

  isScrolling = false;
  iscroll: IScroll = {} as any;
  roomScale: number = 0;
  editTargetRoom: Room = { subRooms: [] } as any;
  editTargetSubroom: SubRoom | null = null;
  original = '';

  roomContainer: HTMLElementWithPosition = {} as HTMLElementWithPosition;

  showConfirmCancellation = false;

  get canApply(): boolean {
    return this.original !== JSON.stringify(this.subrooms);
  }

  mounted(): void {
    this.setRoomContainer();
    window.addEventListener('resize', this.onResize);
    this.subrooms = this.room.subRooms.map((s) => ({ ...s }));
    this.editTargetRoom.subRooms = this.subrooms;
    this.original = JSON.stringify(this.subrooms);
  }

  beforeDestroy(): void {
    window.removeEventListener('resize', this.onResize);
    this.iscroll?.destroy();
  }

  private setRoomContainer(): void {
    const htmlElement: HTMLDivElement = this.$refs.draggableViewport as HTMLDivElement;
    const rect = htmlElement.getBoundingClientRect();

    this.roomContainer = {
      htmlElementRef: htmlElement,
      position: { x: rect.left, y: rect.top },
      width: this.room?.roomSize.width ?? 100,
      heigh: this.room?.roomSize.height ?? 100,
    };
    this.onResize();
  }
  private onResize() {
    if (this.iscroll && this.iscroll.destroy) {
      this.iscroll.destroy();
    }
    this.setupIScroll();
  }

  private setupIScroll() {
    const viewportEl = this.$refs.draggableViewport as HTMLDivElement;
    const minimapEl = this.$refs.minimap as HTMLDivElement;
    const pRect = viewportEl.getBoundingClientRect();
    const wRatio = pRect.width / this.room.roomSize.width;
    const hRatio = pRect.height / this.room.roomSize.height;
    // const zoomMin = wRatio > hRatio ? hRatio : wRatio;
    const zoomMin = Math.max(hRatio, Math.min(1080 / this.room.roomSize.height, 1));
    this.iscroll = new IScroll(viewportEl, {
      zoom: true,
      zoomMin,
      zoomMax: 4,
      scrollX: true,
      scrollY: true,
      mouseWheel: true,
      wheelAction: 'zoom',
      indicators: {
        el: minimapEl,
        interactive: true,
      },
    } as any);
    this.roomScale = zoomMin;
    this.$store.commit('SetRoomScale', this.roomScale);

    this.iscroll.zoom(zoomMin, 1000, 0, 0);
    this.iscroll.on('zoomEnd', (ev) => {
      this.roomScale = this.iscroll.scale;
      this.$store.commit('SetRoomScale', this.roomScale);
    });
    this.iscroll.on('refresh', (ev) => {
      this.roomScale = this.iscroll.scale;
      this.$store.commit('SetRoomScale', this.roomScale);
    });
    this.iscroll.on('scrollStart', (ev) => {
      this.isScrolling = true;
    });
    this.iscroll.on('scrollEnd', (ev) => {
      this.isScrolling = false;
    });
  }
  hidePopup(): void {
    this.close();
  }
  addSubRoom(pos?: CoordinatesPosition) {
    const offset = pos ?? { x: 200, y: 300 };
    const subroom: SubRoom = {
      subid: v4(),
      topLeft: { x: offset.x - 150, y: offset.y - 100 },
      bottomRight: { x: offset.x + 150, y: offset.y + 100 },
      showName: true,
      name: '新しい会議室',
      volume: 1,
      userStates: [],
    };
    this.subrooms.push(subroom);
    this.zoomToSubroom(subroom);
  }
  onSubroomSelectionChanged(info: { rect: any; subroom: SubRoom }) {
    if (info?.subroom) {
      this.zoomToSubroom(info.subroom);
    }
  }
  onDeleteSubroom(subroom: SubRoom) {
    let index = this.subrooms.findIndex((r) => r.subid == subroom.subid);
    if (index >= 0) {
      this.subrooms.splice(index, 1);
    }
  }
  onApplyProps(subroom: SubRoom) {
    if (this.editTargetSubroom) {
      let target = this.editTargetSubroom;
      for (let k of Object.keys(subroom) as (keyof SubRoom)[]) {
        this.$set(target, k, subroom[k]);
      }
    }
    this.editTargetSubroom = null;
  }
  onDblclick(ev: MouseEvent) {
    const scaledPos = {
      x: ev.x - this.roomContainer.position.x - this.iscroll.x,
      y: ev.y - this.roomContainer.position.y - this.iscroll.y,
    };
    const pos = { x: scaledPos.x / this.roomScale, y: scaledPos.y / this.roomScale };
    this.addSubRoom(pos);
  }
  private zoomToSubroom(subroom: SubRoom): void {
    let viewportWidth = window.innerWidth - 240 - 250;
    let viewportHeight = window.innerHeight;
    if (this.iscroll.scale == 1) {
    } else {
      this.iscroll.zoom(1, 0, 0, 300);
    }
    const x = (subroom.topLeft.x + subroom.bottomRight.x) / 2;
    const y = (subroom.topLeft.y + subroom.bottomRight.y) / 2;

    this.iscroll.scrollTo((viewportWidth / 2 - x) * this.roomScale, (viewportHeight / 2 - y) * this.roomScale, 300);
  }
  closeOrShowConfirmCancellation(): void {
    if (this.canApply) {
      this.showConfirmCancellation = true;
    } else {
      this.close();
    }
  }
  closeConfirmCancellation(): void {
    this.showConfirmCancellation = false;
  }
}
